import React from "react";
import {
  View,
  Pressable,
  Image,
  ViewStyle,
  ImageStyle,
  DimensionValue,
  AnimatableNumericValue,
  AccessibilityRole,
} from "react-native";
import { BoxProps } from "@xsolla/xui-primitives-core";

const roleToAccessibilityRole: Record<string, AccessibilityRole> = {
  button: "button",
  link: "link",
  img: "image",
  image: "image",
  summary: "summary",
  switch: "switch",
  checkbox: "checkbox",
  radio: "radio",
  heading: "header",
  text: "text",
  search: "search",
  alert: "alert",
  none: "none",
  presentation: "none",
  progressbar: "progressbar",
  scrollbar: "scrollbar",
  adjustable: "adjustable",
  tab: "tab",
  menu: "menu",
  menuitem: "menuitem",
  list: "list",
  timer: "timer",
};

/** RN ViewStyle.gap is typed as number (Yoga gap), not DimensionValue. */
const toGapValue = (gap: number | string | undefined): number | undefined => {
  if (gap === undefined) return undefined;
  if (typeof gap === "number") return gap;
  const pxMatch = /^(\d+(?:\.\d+)?)px$/.exec(gap);
  if (pxMatch) return Number(pxMatch[1]);
  const asNumber = Number(gap);
  return Number.isFinite(asNumber) ? asNumber : undefined;
};

export const Box: React.FC<BoxProps> = ({
  children,
  onPress,
  onLayout,
  onMoveShouldSetResponder,
  onResponderGrant,
  onResponderMove,
  onResponderRelease,
  onResponderTerminate,
  backgroundColor,
  borderColor,
  borderWidth,
  borderBottomWidth,
  borderBottomColor,
  borderTopWidth,
  borderTopColor,
  borderLeftWidth,
  borderLeftColor,
  borderRightWidth,
  borderRightColor,
  borderRadius,
  borderStyle,
  height,
  padding,
  paddingHorizontal,
  paddingVertical,
  margin,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  flexDirection,
  flexWrap,
  alignItems,
  justifyContent,
  alignSelf,
  position,
  top,
  bottom,
  left,
  right,
  width,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  flex,
  flexShrink,
  gap,
  overflow,
  opacity,
  zIndex,
  hoverStyle,
  pressStyle,
  style,
  disabled,
  "data-testid": dataTestId,
  testID,
  as,
  src,
  alt,
  ...rest
}) => {
  const getContainerStyle = (pressed?: boolean): ViewStyle => ({
    backgroundColor:
      pressed && pressStyle?.backgroundColor
        ? pressStyle.backgroundColor
        : backgroundColor,
    borderColor,
    borderWidth,
    borderBottomWidth,
    borderBottomColor,
    borderTopWidth,
    borderTopColor,
    borderLeftWidth,
    borderLeftColor,
    borderRightWidth,
    borderRightColor,
    borderRadius: borderRadius as AnimatableNumericValue,
    borderStyle: borderStyle as ViewStyle["borderStyle"],
    overflow,
    opacity: opacity ?? (disabled ? 0.5 : undefined),
    zIndex,
    height: height as DimensionValue,
    width: width as DimensionValue,
    minWidth: minWidth as DimensionValue,
    minHeight: minHeight as DimensionValue,
    maxWidth: maxWidth as DimensionValue,
    maxHeight: maxHeight as DimensionValue,
    padding: padding as DimensionValue,
    paddingHorizontal: paddingHorizontal as DimensionValue,
    paddingVertical: paddingVertical as DimensionValue,
    margin: margin as DimensionValue,
    marginTop: marginTop as DimensionValue,
    marginBottom: marginBottom as DimensionValue,
    marginLeft: marginLeft as DimensionValue,
    marginRight: marginRight as DimensionValue,
    flexDirection,
    flexWrap,
    alignItems,
    justifyContent,
    alignSelf,
    position: position as ViewStyle["position"],
    top: top as DimensionValue,
    bottom: bottom as DimensionValue,
    left: left as DimensionValue,
    right: right as DimensionValue,
    flex,
    flexShrink,
    gap: toGapValue(gap),
    ...(style as ViewStyle),
  });

  const finalTestID = dataTestId || testID;

  // Map web a11y props; drop the rest of the web-only attributes before
  // spreading onto RN hosts (unknown props warn at runtime).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    role,
    tabIndex,
    onKeyDown,
    onKeyUp,
    "aria-label": ariaLabel,
    "aria-labelledby": _ariaLabelledBy,
    "aria-current": _ariaCurrent,
    "aria-disabled": ariaDisabled,
    "aria-live": ariaLive,
    "aria-busy": ariaBusy,
    className,
    "data-testid": _dataTestId,
    cursor: _cursor,
    type: _type,
    ...nativeRest
  } = rest as Record<string, unknown>;

  const accessibilityRole =
    (typeof role === "string" && roleToAccessibilityRole[role]) ||
    (as === "button" ? "button" : undefined);
  const isDisabled = Boolean(disabled || ariaDisabled);
  const accessibilityState = {
    disabled: isDisabled || undefined,
    busy: Boolean(ariaBusy) || undefined,
  };
  const accessibilityLiveRegion =
    ariaLive === "polite" || ariaLive === "assertive" ? ariaLive : undefined;

  // Handle as="img" for React Native
  if (as === "img" && src) {
    const imageStyle: ImageStyle = {
      width: width as DimensionValue,
      height: height as DimensionValue,
      borderRadius: borderRadius as number,
      position: position as ImageStyle["position"],
      top: top as DimensionValue,
      bottom: bottom as DimensionValue,
      left: left as DimensionValue,
      right: right as DimensionValue,
      ...(style as ImageStyle),
    };

    return (
      <Image
        source={{ uri: src }}
        style={imageStyle}
        testID={finalTestID}
        accessibilityLabel={typeof ariaLabel === "string" ? ariaLabel : alt}
        resizeMode="cover"
        {...nativeRest}
      />
    );
  }

  // Prefer Pressable whenever the node is interactive or explicitly a button,
  // so disabled / aria-disabled controls stay in the a11y tree as buttons.
  if (onPress || as === "button") {
    return (
      <Pressable
        onPress={!isDisabled && onPress ? onPress : undefined}
        disabled={isDisabled}
        onLayout={onLayout}
        onMoveShouldSetResponder={onMoveShouldSetResponder}
        onResponderGrant={onResponderGrant}
        onResponderMove={onResponderMove}
        onResponderRelease={onResponderRelease}
        onResponderTerminate={onResponderTerminate}
        style={({ pressed }) => getContainerStyle(pressed)}
        testID={finalTestID}
        accessibilityRole={accessibilityRole}
        accessibilityLabel={
          typeof ariaLabel === "string" ? ariaLabel : undefined
        }
        accessibilityState={accessibilityState}
        accessibilityLiveRegion={accessibilityLiveRegion}
        {...nativeRest}
      >
        {children}
      </Pressable>
    );
  }

  return (
    <View
      style={getContainerStyle()}
      testID={finalTestID}
      onLayout={onLayout}
      onMoveShouldSetResponder={onMoveShouldSetResponder}
      onResponderGrant={onResponderGrant}
      onResponderMove={onResponderMove}
      onResponderRelease={onResponderRelease}
      onResponderTerminate={onResponderTerminate}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={typeof ariaLabel === "string" ? ariaLabel : undefined}
      accessibilityState={accessibilityState}
      accessibilityLiveRegion={accessibilityLiveRegion}
      {...nativeRest}
    >
      {children}
    </View>
  );
};
